import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { API_BASE } from '../../config/api';

const formatDateInputValue = (value) => {
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const defaultFilters = () => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - 6);
  return {
    from: formatDateInputValue(from),
    to: formatDateInputValue(today)
  };
};

const read = (row, ...keys) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
};

const readJsonResponse = async (response) => {
  const text = await response.text();
  const contentType = response.headers.get('content-type') || '';
  const trimmed = text.trim();
  const looksJson = trimmed.startsWith('{') || trimmed.startsWith('[');

  if (!contentType.includes('application/json') && !looksJson) {
    throw new Error(`Voicemail API returned HTML instead of JSON. Check backend route/migration: ${response.url}`);
  }

  try {
    return trimmed ? JSON.parse(trimmed) : {};
  } catch (err) {
    throw new Error(`Voicemail API returned invalid JSON: ${err.message}`);
  }
};

const columns = [
  { name: 'Voicemail At', selector: row => read(row, 'VoicemailAt'), sortable: true, grow: 1.4 },
  { name: 'Number', selector: row => read(row, 'CallerNumber'), sortable: true },
  { name: 'Contact', selector: row => read(row, 'ContactName', 'ComplaintContactPerson'), sortable: true, grow: 1.2 },
  { name: 'Complaint', selector: row => read(row, 'ComplaintId', 'QueryId'), sortable: true },
  { name: 'Queue', selector: row => read(row, 'QueueName'), sortable: true },
  { name: 'Call ID', selector: row => read(row, 'UniqueId'), sortable: true, grow: 1.5 },
];

const VoicemailTab = ({ onRowClick, reloadKey = 0 }) => {
  const [filters, setFilters] = useState(() => defaultFilters());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      const text = String(value || '').trim();
      if (text) params.set(key, text);
    });
    params.set('limit', '50');
    return params.toString();
  }, [filters]);

  const loadVoicemails = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/voicemails?${queryString}`, {
        headers: { Accept: 'application/json' }
      });
      const payload = await readJsonResponse(response);
      if (!response.ok) {
        throw new Error(payload?.message || payload?.error || 'Failed to load voicemails');
      }
      setRows(Array.isArray(payload?.data) ? payload.data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load voicemails');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    loadVoicemails();
  }, [loadVoicemails, reloadKey]);

  const updateFilter = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div>
      <div className="row g-2 align-items-end mb-3">
        <div className="col-6 col-lg-3">
          <label className="form-label small fw-semibold">From</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={filters.from}
            onChange={(event) => updateFilter('from', event.target.value)}
          />
        </div>
        <div className="col-6 col-lg-3">
          <label className="form-label small fw-semibold">To</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={filters.to}
            onChange={(event) => updateFilter('to', event.target.value)}
          />
        </div>
        <div className="col-12 col-lg-2">
          <button type="button" className="btn btn-sm btn-primary w-100" onClick={loadVoicemails}>
            <i className="bi bi-arrow-clockwise"></i>
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <DataTable
        columns={columns}
        data={rows}
        progressPending={loading}
        pagination
        highlightOnHover
        pointerOnHover
        dense
        striped
        noDataComponent="No voicemail calls found"
        onRowClicked={onRowClick}
      />
    </div>
  );
};

export default VoicemailTab;
